import { ColDef, ColGroupDef, GridOptions } from 'ag-grid-community'
import { IndexDetail } from 'api'
import {
  createActionsRenderer,
  createGroupedActionRenderer,
  gridOptionsBase,
  groupingRowColDef,
  StatusCellRenderer,
  TypedValueGetterParams,
  withAllContext
} from 'view/components/datagrid'
import { IndexGridColumnId } from './IndexGridColumnNames'
import { IndexActionsButton } from './IndexActionsButton'
import { getEntityStatusActiveSuspendedText, getEntityStatusColor, makeDateAndTimeFormatter } from 'view/shared/datagrid'
import { newestPriceGetter, newestValidForGetter, priceFormatter, validForFormatter } from './indexFormatters'
import { Locale, Translation } from 'locale'

export const IndexColumnDefs: (ColGroupDef | ColDef)[] = [
  {
    colId: IndexGridColumnId.INDEX_CHECKBOX,
    headerName: Locale.translate(Translation.TC_EMPTY_HEADER),
    headerCheckboxSelection: true,
    headerCheckboxSelectionFilteredOnly: true,
    checkboxSelection: true,
    width: 36,
    maxWidth: 36,
    pinned: 'left',
    lockPinned: true,
    enableRowGroup: false,
    lockPosition: true,
    lockVisible: true,
    filter: false,
    resizable: false,
    sortable: false
  },
  groupingRowColDef,
  {
    colId: IndexGridColumnId.INDEX_ACTIONS,
    headerName: Locale.translate(Translation.TC_EMPTY_HEADER),
    equals: () => false,
    width: 130,
    minWidth: 130,
    headerComponent: withAllContext(createGroupedActionRenderer(IndexActionsButton)),
    cellClass: 'action-cell',
    cellRenderer: withAllContext(createActionsRenderer(IndexActionsButton)),
    pinned: 'left',
    lockPinned: true,
    enableRowGroup: false,
    lockVisible: true,
    filter: false,
    sortable: false
  },
  {
    colId: IndexGridColumnId.INDEX_STATUS,
    valueGetter: (params: TypedValueGetterParams<IndexDetail>) =>
      params.data && getEntityStatusActiveSuspendedText(params.data.index.status),
    headerName: Locale.translate(Translation.TC_STATUS),
    width: 170,
    filter: 'agSetColumnFilter',
    cellRenderer: StatusCellRenderer,
    cellRendererParams: {
      getStatusColor: (data: IndexDetail) => getEntityStatusColor(data.index.status)
    },
    equals: () => false,
    lockVisible: true,
    lockPosition: true
  },
  {
    colId: IndexGridColumnId.INDEX_DESCRIPTION,
    valueGetter: (params: TypedValueGetterParams<IndexDetail>) => params.data?.index.description,
    headerName: Locale.translate(Translation.TC_INDEX_DESCRIPTION),
    width: 150,
    filter: 'agSetColumnFilter'
  },
  {
    colId: IndexGridColumnId.INDEX_BLOOMBERG_CODE,
    valueGetter: (params: TypedValueGetterParams<IndexDetail>) => params.data?.index.code,
    headerName: Locale.translate(Translation.TC_BLOOMBERG_CODE),
    width: 120,
    filter: 'agSetColumnFilter'
  },
  {
    colId: IndexGridColumnId.INDEX_VALID_FOR,
    valueGetter: newestValidForGetter,
    valueFormatter: validForFormatter,
    headerName: Locale.translate(Translation.TC_VALID_FOR),
    width: 150,
    filter: 'agSetColumnFilter'
  },
  {
    colId: IndexGridColumnId.INDEX_PRICE,
    valueGetter: newestPriceGetter,
    valueFormatter: priceFormatter,
    headerName: Locale.translate(Translation.TC_PRICE),
    width: 120,
    filter: 'agSetColumnFilter'
  },
  {
    colId: IndexGridColumnId.INDEX_ISIN,
    valueGetter: (params: TypedValueGetterParams<IndexDetail>) => params.data?.index.isin,
    headerName: Locale.translate(Translation.TC_ISIN),
    width: 120,
    filter: 'agSetColumnFilter'
  },
  {
    colId: IndexGridColumnId.INDEX_BLOOMBERG_INDEX_CODE,
    valueGetter: (params: TypedValueGetterParams<IndexDetail>) => params.data?.index.refinitivBaseIndex,
    headerName: Locale.translate(Translation.TC_REFINITIV_BASE_INDEX),
    width: 120,
    filter: 'agSetColumnFilter'
  },
  {
    colId: IndexGridColumnId.INDEX_LAG_AMOUNT,
    valueGetter: (params: TypedValueGetterParams<IndexDetail>) => params.data?.index.lagAmount,
    headerName: Locale.translate(Translation.TC_LAG_AMOUNT),
    width: 120,
    filter: 'agSetColumnFilter'
  },
  {
    colId: IndexGridColumnId.INDEX_LAG_TYPE,
    valueGetter: (params: TypedValueGetterParams<IndexDetail>) => params.data?.index.lagType,
    headerName: Locale.translate(Translation.TC_LAG_TYPE),
    width: 120,
    filter: 'agSetColumnFilter'
  },
  {
    colId: IndexGridColumnId.INDEX_LAST_UPDATED_AT,
    valueGetter: (params: TypedValueGetterParams<IndexDetail>) => params.data?.index.lastUpdatedAt,
    valueFormatter: makeDateAndTimeFormatter(),
    headerName: Locale.translate(Translation.TC_LAST_UPDATED_AT),
    width: 150,
    filter: 'agSetColumnFilter'
  },
  {
    colId: IndexGridColumnId.INDEX_LAST_UPDATED_BY,
    valueGetter: (params: TypedValueGetterParams<IndexDetail>) => params.data?.lastUpdatedByUserName,
    headerName: Locale.translate(Translation.TC_LAST_UPDATED_BY),
    width: 230,
    filter: 'agSetColumnFilter'
  }
]

export const indexGridOptions: GridOptions = {
  ...gridOptionsBase,
  getRowId: ({ data }) => data.index.id
}
